import { config } from '../../config';
import { MATCH_STATE } from '../machine';
import { ok, reject } from '../result';
import { ARTIFACT_PHASE } from '../../types';
import type { AdjustmentEntry, CoachCardContext, MatchState } from '../../types';
import type {
  CoachCardOp,
  Decision,
  DiscardCoachCardDraftCommand,
  DiscardCoachCardDraftSnapshot,
  FinalizeCoachCardCommand,
  FinalizeCoachCardSnapshot,
  UpsertCoachCardDraftCommand,
  UpsertCoachCardDraftSnapshot,
} from './types';

/**
 * B spec §4.2, scenarios 22–32 — **the coach card commands** (build unit B-3): the persistence
 * layer of the adjustments workflow. §2.5's rule is *persistence first, db-driven, no zombie
 * states*: every structural act commits the whole draft immediately through
 * `UpsertCoachCardDraft`, finalize is a one-way status flip, discard is the one-tap resolution
 * F-1's completion guard (unit B-18) depends on.
 *
 * ── `Decision<CoachCardOp>` is the workflow's blast-radius claim ─────────────
 *
 * A card handler cannot compile a count change, a status override, a board position, an
 * Observation, a transition, or a talk-point write ({@link CoachCardOp} is a one-table extract).
 * The last of those is load-bearing for the reuse contract: `coachCards` replaces the frozen
 * distillation output, and an op set that could reach `writeTalkPointSelection` would let the
 * replacement write into the machinery it replaces.
 *
 * ── The windows, once, as data ───────────────────────────────────────────────
 *
 * §4.2: a `halftime` card is writable while state ∈ {halftime, fullTime} — the whistled-away
 * draft is finished after the match (§2.5's boundary behavior) — and a `fullTime` card at
 * `fullTime` only. Both exclude `archived`; F-1's guard refuses to archive over a draft for
 * exactly that reason. One map serves all three handlers, so the three cannot disagree about
 * what a context's window is.
 */

const MODULE = 'commands/coachCard';

/** §4.2's per-context draft windows. See the module header for why `fullTime` is in both. */
const DRAFT_WINDOW: Record<CoachCardContext, ReadonlySet<MatchState>> = {
  [ARTIFACT_PHASE.halftime]: new Set<MatchState>([MATCH_STATE.halftime, MATCH_STATE.fullTime]),
  [ARTIFACT_PHASE.fullTime]: new Set<MatchState>([MATCH_STATE.fullTime]),
};

/**
 * §2.5 step 3 / scenario 29 — would this entry render on the finalized card?
 *
 * `'accepted'` is the distinct persisted fact acceptance is; a manual entry is the user's own
 * words and needs no acceptance, but a rejected one is excluded whatever its origin — the
 * scenario-29 negative ("rejected entries absent") admits no carve-out for authorship.
 *
 * **Exported for B-15's "Make the card (n)".** The workflow needs the same count this guard
 * enforces, and a second copy of the rule in the UI is how a button that says *(3)* comes to
 * finalize a two-entry card. One predicate, two readers: this handler and the surface.
 */
export function rendersOnCard(entry: AdjustmentEntry): boolean {
  if (entry.status === 'rejected') return false;
  return entry.status === 'accepted' || entry.origin === 'manual';
}

/**
 * B §4.2 — create or edit the one draft for (match, context), whole-row, at interaction grain.
 *
 * ── The order of the guards is the order of the questions ────────────────────
 *
 * Match, then window, then *may this purpose hold this card at all*, then *does the row make
 * sense*, then *what does this break already hold*, then the bounds, then the sources — each a
 * different sentence to the user, each read from the snapshot the transaction opened with (the
 * `handleWriteAnnotation` discipline).
 */
export function handleUpsertCoachCardDraft(
  cmd: UpsertCoachCardDraftCommand,
  snap: UpsertCoachCardDraftSnapshot,
): Decision<CoachCardOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }

  const { row } = cmd;

  if (!DRAFT_WINDOW[row.context].has(match.state)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_CARD_WINDOW',
      row.context === ARTIFACT_PHASE.halftime
        ? 'A halftime card is built at the break or after the match, not during play.'
        : 'A full-time card is built after the final whistle.',
      { state: match.state, cardContext: row.context, id: row.id },
    );
  }

  // Scenario 20's negative, at the command grain: an advance scout's halftime is break only —
  // no workflow entry renders there, and no card is creatable even by a surface that forgot.
  if (row.context === ARTIFACT_PHASE.halftime && match.purpose === 'advanceScout') {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_CARD_CONTEXT',
      'An advance scout has no halftime adjustments — points of emphasis are built at full time.',
      { purpose: match.purpose, cardContext: row.context, id: row.id },
    );
  }

  if (row.matchId !== cmd.matchId) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_COMMAND_ROW_MISMATCH',
      'That card could not be saved because it names a different match.',
      { id: row.id, rowMatchId: row.matchId },
    );
  }

  // This command writes *drafts*. A `status:'final'` row here would be a second way to finalize
  // — `FinalizeCoachCard` is the first, and two exits is how one ends up forgotten (the
  // `handleCaptureSetPiece` argument for refusing `draft:false`). A non-null `finalizedAt` on a
  // draft is the same contradiction one field over.
  if (row.status !== 'draft' || row.finalizedAt !== null) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_COMMAND_ROW_MISMATCH',
      'A card is finalized through its own control, not through a draft save.',
      { id: row.id, status: row.status, finalizedAt: row.finalizedAt },
    );
  }

  // The id crossed a UI boundary, so the stored row's provenance is re-checked rather than
  // trusted: an id that already names another match's card, or this match's *other* break, would
  // be re-homed by the `put` beneath this handler — a rewrite of a row the user is not looking at.
  const byId = snap.existingById;
  if (byId !== undefined && (byId.matchId !== cmd.matchId || byId.context !== row.context)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_COMMAND_ROW_MISMATCH',
      'That card could not be saved because its id already belongs to a different card.',
      { id: row.id, storedMatchId: byId.matchId, storedContext: byId.context },
    );
  }

  const existing = snap.existingForContext;
  if (existing !== undefined) {
    if (existing.status === 'final') {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_CARD_FINALIZED',
        'This card was already made — a finalized card never changes.',
        { id: existing.id, cardContext: row.context },
      );
    }

    // ≤1 card per (match, context): the unique `&[matchId+context]` index (B-1) enforces it at
    // the DB grain; this is the renderable answer for a caller minting a second draft where one
    // already exists, instead of a `ConstraintError` surfacing as a storage fault.
    if (existing.id !== row.id) {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_COMMAND_ROW_MISMATCH',
        'This break already holds a card — reopen it rather than starting another.',
        { id: row.id, existingId: existing.id, cardContext: row.context },
      );
    }
  }

  // ── ERR_CARD_TOO_LARGE bounds ADJUSTMENTS ONLY (§4.2) ─────────────────────
  // `selectedHypothesisIds` is deliberately unbounded — scenario 23's "zero to all, no cap" —
  // so no branch here may count the selection. The two bounds are §4.3's constants.
  if (row.adjustments.length > config.COACH_CARD_MAX_ENTRIES) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_CARD_TOO_LARGE',
      `A card holds at most ${String(config.COACH_CARD_MAX_ENTRIES)} adjustments.`,
      { id: row.id, entryCount: row.adjustments.length, cap: config.COACH_CARD_MAX_ENTRIES },
    );
  }
  const oversized = row.adjustments.find(
    (entry) => entry.text.length > config.COACH_ADJUSTMENT_MAX_CHARS,
  );
  if (oversized !== undefined) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_CARD_TOO_LARGE',
      `An adjustment holds at most ${String(config.COACH_ADJUSTMENT_MAX_CHARS)} characters.`,
      {
        id: row.id,
        entryId: oversized.entryId,
        textLength: oversized.text.length,
        cap: config.COACH_ADJUSTMENT_MAX_CHARS,
      },
    );
  }

  // ERR_CARD_SOURCE — every hypothesis the card cites must be this match's. One code for a
  // selection id and an adjustment's source id: both are the "because —" line's provenance, and
  // a card citing a deleted or foreign row is §3.2's blank line one workflow over.
  const known = new Set(snap.hypothesisIds);
  const unknownSelection = row.selectedHypothesisIds.find((id) => !known.has(id));
  if (unknownSelection !== undefined) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_CARD_SOURCE',
      'That pattern is no longer on this match, so it cannot be selected for the card.',
      { id: row.id, hypothesisId: unknownSelection },
    );
  }
  for (const entry of row.adjustments) {
    const unknownSource = entry.sourceHypothesisIds.find((id) => !known.has(id));
    if (unknownSource !== undefined) {
      return reject(
        MODULE,
        cmd.matchId,
        'ERR_CARD_SOURCE',
        'That adjustment cites a pattern that is no longer on this match.',
        { id: row.id, entryId: entry.entryId, hypothesisId: unknownSource },
      );
    }
  }

  // The stored row: `createdAt` preserved from the in-transaction read (a stale render cannot
  // re-date the card), `updatedAt` stamped from the snapshot's clock (never trusted from a
  // render that may be a tap out of date — the `updateOurPlayer` discipline, whole-row form).
  const stored = {
    ...row,
    createdAt: existing?.createdAt ?? row.createdAt,
    updatedAt: snap.now,
  };

  return ok({
    ops: [{ t: 'upsertCoachCardDraft', row: stored }],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg: existing === undefined ? 'coach card draft created' : 'coach card draft written',
        data: {
          id: row.id,
          cardContext: row.context,
          selectionCount: row.selectedHypothesisIds.length,
          entryCount: row.adjustments.length,
        },
      },
    ],
    post: [],
  });
}

/**
 * B §4.2 — make the card. The one write is the status flip ({@link CoachCardOp}'s
 * `finalizeCoachCard`); what is frozen is exactly what the last draft commit stored.
 */
export function handleFinalizeCoachCard(
  cmd: FinalizeCoachCardCommand,
  snap: FinalizeCoachCardSnapshot,
): Decision<CoachCardOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }

  // One sentence for both absences (missing, or another match's row) — naming a foreign row's
  // existence would leak another match's data into this one's error.
  const existing = snap.existing;
  if (existing === undefined || existing.matchId !== cmd.matchId) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_CARD_NOT_FOUND',
      'That card is no longer on this match.',
      { id: cmd.cardId },
    );
  }

  if (existing.status === 'final') {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_CARD_FINALIZED',
      'This card was already made — a finalized card never changes.',
      { id: existing.id, cardContext: existing.context },
    );
  }

  if (!DRAFT_WINDOW[existing.context].has(match.state)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_CARD_WINDOW',
      existing.context === ARTIFACT_PHASE.halftime
        ? 'A halftime card is finished at the break or after the match, not during play.'
        : 'A full-time card is finished after the final whistle.',
      { state: match.state, cardContext: existing.context, id: existing.id },
    );
  }

  // Scenario 29's guard: at least one entry that would render. An all-suggested or all-rejected
  // draft finalizing into a blank card would satisfy every per-entry negative and still be wrong.
  if (!existing.adjustments.some(rendersOnCard)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_CARD_EMPTY',
      'Accept a suggestion or write an adjustment of your own first — an empty card says nothing.',
      { id: existing.id, entryCount: existing.adjustments.length },
    );
  }

  return ok({
    ops: [{ t: 'finalizeCoachCard', id: existing.id }],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg: 'coach card finalized',
        data: {
          id: existing.id,
          cardContext: existing.context,
          renderedEntryCount: existing.adjustments.filter(rendersOnCard).length,
        },
      },
    ],
    post: [],
  });
}

/**
 * B §4.2 — discard the draft. A finalized card can never be discarded (`ERR_CARD_FINALIZED`);
 * the window binds a discard exactly as it binds a write — an archived record loses a draft as
 * illegally as it gains one, which is why F-1 resolves drafts *before* archiving.
 */
export function handleDiscardCoachCardDraft(
  cmd: DiscardCoachCardDraftCommand,
  snap: DiscardCoachCardDraftSnapshot,
): Decision<CoachCardOp> {
  const match = snap.match;
  if (match === undefined) {
    return reject(MODULE, cmd.matchId, 'ERR_MATCH_NOT_FOUND', 'That match no longer exists.');
  }

  const existing = snap.existing;
  if (existing === undefined || existing.matchId !== cmd.matchId) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_CARD_NOT_FOUND',
      'That card is no longer on this match.',
      { id: cmd.cardId },
    );
  }

  if (existing.status === 'final') {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_CARD_FINALIZED',
      'A finalized card is part of the match record and cannot be discarded.',
      { id: existing.id, cardContext: existing.context },
    );
  }

  if (!DRAFT_WINDOW[existing.context].has(match.state)) {
    return reject(
      MODULE,
      cmd.matchId,
      'ERR_CARD_WINDOW',
      existing.context === ARTIFACT_PHASE.halftime
        ? 'A halftime draft is discarded at the break or after the match, not during play.'
        : 'A full-time draft is discarded after the final whistle.',
      { state: match.state, cardContext: existing.context, id: existing.id },
    );
  }

  return ok({
    ops: [{ t: 'deleteCoachCardDraft', id: existing.id }],
    log: [
      {
        module: MODULE,
        level: 'info',
        matchId: cmd.matchId,
        msg: 'coach card draft discarded',
        data: { id: existing.id, cardContext: existing.context },
      },
    ],
    post: [],
  });
}
